<script setup lang="ts">
import type { ProfileUpdate } from "@profile-service-types/index.ts";
import { useForm } from "@tanstack/vue-form";
import { useToggle } from "@vueuse/core";
import { cloneDeep } from "lodash";
import { computed, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import AdvisorProfileProperties from "@/components/Account/components/AdvisorProfileProperties/index.vue";
import SharedProfileProperties from "@/components/Account/components/SharedProfileProperties.vue";
import UserPermissionsComponent from "@/components/Account/components/UserPermissionsComponent/index.vue";
import { LooseProfile } from "@/components/Account/profileSchema.ts";
import type { ProfileFieldsUpdate, ZodProfileType } from "@/components/Account/types.ts";
import ErrorPage from "@/components/errorPage.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import EditableForm from "@/components/shared/EditableForm.vue";
import { hasUsersWritePermission, isAdvisorOrEmployee, isTenantManager } from "@/composables/authorization";
import { ERROR_TIMEOUT, PERMISSIONS, SUCCESS_TIMEOUT } from "@/constants.js";
import { useGetProfileDetails } from "@/providers/profiles/useGetSingleProfile.ts";
import { useUpdateProfile } from "@/providers/profiles/useUpdateProfile";

const route = useRoute();
const toast = useToast();
const store = useStore();

interface LocalState {
  editProfile?: ProfileUpdate;
  cancelled: boolean;
}

const localState = reactive<LocalState>({
  editProfile: undefined,
  cancelled: false,
});

const [isEditing, toggleEditing] = useToggle();

const routeParticipantId = computed(() => route?.params?.profileId);

const userIsTenantManager = computed<boolean>(isTenantManager);
const userIsAdvisorOrEmployee = computed<boolean>(isAdvisorOrEmployee);

const oktaId = computed(() =>
  Boolean(store.state?.featureFlags?.["user-management"]) && routeParticipantId.value
    ? routeParticipantId.value
    : store?.state?.currentUser?.participantId,
);

const {
  data: profileData,
  isLoading: profileLoading,
  error: profileError,
} = useGetProfileDetails({ id: oktaId, useQueryOptions: { staleTime: 0 } });

const { mutateAsync, data: profileUpdateData, isPending: profileUpdatePending } = useUpdateProfile();

const userHasEditPermission = computed(() => {
  return hasUsersWritePermission.value || profileData?.value?.okta_id === oktaId.value;
});

const form = useForm<
  ProfileFieldsUpdate,
  undefined,
  ZodProfileType,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined
>({
  defaultValues: {
    bio: localState.editProfile?.bio,
    department: localState.editProfile?.department,
    email: localState.editProfile?.email ?? "",
    secondary_email: localState.editProfile?.secondary_email ?? "",
    headshot: localState.editProfile?.headshot,
    job_title: localState.editProfile?.job_title,
    last_name: localState.editProfile?.last_name,
    legal_first_name: localState.editProfile?.legal_first_name,
    location_city_id: localState.editProfile?.location_city_id,
    phone_number: localState.editProfile?.phone_number,
    secondary_phone_number: localState.editProfile?.secondary_phone_number,
    preferred_first_name: localState.editProfile?.preferred_first_name,
    pronouns: localState.editProfile?.pronouns,
    work_location_city_id: localState.editProfile?.work_location_city_id,
  },
  validators: {
    onChange: LooseProfile,
  },
  onSubmit: async ({ value, formApi }) => {
    if (profileData.value) {
      try {
        const payload = { ...profileData.value, ...value };
        payload.phone_number = payload.phone_number?.replace(/\D/g, "") ?? null;
        payload.secondary_phone_number = payload.secondary_phone_number?.replace(/\D/g, "") ?? null;
        const updateResponse = await mutateAsync({ id: oktaId, payload });
        store.commit("setProfileData", updateResponse);
        toast.open({
          message: "Profile saved successfully.",
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
        toggleEditing();
      } catch (error) {
        toast.open({
          message: "Failed to save profile.",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    }
  },
});

const cancelEdit = async () => {
  localState.editProfile = cloneDeep(profileData.value);
  localState.cancelled = true;
  form.reset(profileData.value);
  toggleEditing();
};

const editForm = () => {
  localState.cancelled = false;
  toggleEditing();
};

watch(
  profileData,
  (updatedProfile, originalProfile) => {
    if (updatedProfile) {
      store.commit("setProfileData", cloneDeep(updatedProfile));
      localState.editProfile = cloneDeep(updatedProfile);
      form.reset(localState.editProfile);
    }
  },
  { immediate: true },
);
</script>

<template>
  <EditableForm
    v-if="profileData?.id && !profileLoading && !profileError && oktaId && localState.editProfile"
    :is-editing="isEditing"
    form-title="ABOUT YOU"
    :editable="userHasEditPermission"
    @edit-form="editForm"
    @save-form="form.handleSubmit"
    @cancel-edit-form="cancelEdit"
    :parent-form="form"
  >
    <div>
      <shared-profile-properties :editable="isEditing" v-model="localState.editProfile" :original-profile="profileData" :parent-form="form" :has-email-update-permission="hasUsersWritePermission" />
      <advisor-profile-properties v-if="userIsAdvisorOrEmployee" class="" :editable="isEditing" v-model="localState.editProfile" :okta-id="oktaId" :original-profile="profileData" :parent-form="form"/>
      <user-permissions-component v-if="userIsTenantManager" class="" :editable="isEditing" v-model="localState.editProfile" :original-profile="profileData" :parent-form="form" :cancelled="localState.cancelled"/>
    </div>

  </EditableForm>
  <Spinner
    v-else-if="(profileLoading || !localState.editProfile) && !profileError"
    class="m-auto w-full h-full !fixed opacity-75 bg-blue-100 !z-0"
  />
  <ErrorPage
    v-else
    class="relative h-65vh"
    message="Failed to load profile data."
  />
</template>
